import { useCallback } from 'react';
import type { SessionFeatures } from './authClient.js';
import { useSession } from './useSession.js';

/**
 * The second client-side gate, alongside `useAccess`.
 *
 * `useAccess` answers "may this USER reach that destination" from their role and permission keys.
 * This one answers a different question the same nav has to ask: "does this TENANT have that
 * destination at all". ServiceTitan is an integration built for one customer, so for every other
 * tenant — including every workspace that signs up from now on — the page does not exist, whatever
 * the signed-in user's role is. Rolling it into an `AccessRule` would have meant threading the
 * whole session through `grantsAccess`, which takes a user by design.
 *
 * HIDING IS UX, NOT AUTHORIZATION. The api worker's `serviceTitanGate` 404s the same surface from
 * the server side, and that is the boundary. This only keeps a tenant from being shown a nav item
 * that leads nowhere.
 */

/** Destination prefix → the tenant feature that has to be on for it to exist. */
const FEATURE_ROUTE_PREFIXES: ReadonlyArray<readonly [string, keyof SessionFeatures]> = [
  ['/servicetitan', 'servicetitan'],
];

/**
 * Whether the TENANT has a feature at all — the flag-level twin of `tenantHasRoute`, which
 * resolves a destination to its feature first. The platform shell asks this directly
 * (`tenantHasFeature(features, 'new_shell')`): the flag switches the whole chrome, not one
 * route, so there is no destination prefix to resolve. An absent `features` object reads as
 * OFF, exactly like every other flag — an older worker or a failed read keeps the shell
 * today's sidebar rather than revealing something the api worker may not serve.
 */
export function tenantHasFeature(
  features: SessionFeatures | null | undefined,
  feature: keyof SessionFeatures,
): boolean {
  return features?.[feature] === true;
}

/** The feature a destination depends on, or null when it depends on none. */
export function featureForRoute(pathname: string): keyof SessionFeatures | null {
  for (const [prefix, feature] of FEATURE_ROUTE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return feature;
  }
  return null;
}

/**
 * Pure resolver — no React, so tests can call it directly. A destination behind no feature is
 * always shown; one behind a feature is shown only when the session says the tenant has it, so an
 * absent `features` object hides rather than reveals.
 */
export function tenantHasRoute(
  features: SessionFeatures | null | undefined,
  pathname: string,
): boolean {
  const feature = featureForRoute(pathname);
  if (!feature) return true;
  return tenantHasFeature(features, feature);
}

/** Whether the CURRENT session's tenant has the feature. Use it to decide what RENDERS. */
export function useFeatureFlag(feature: keyof SessionFeatures): boolean {
  const { session } = useSession();
  const features = session?.features ?? null;
  return tenantHasFeature(features, feature);
}

/** Returns a stable predicate for the current session. Use it to decide whether to RENDER. */
export function useFeatureAccess(): (pathname: string) => boolean {
  const { session } = useSession();
  const features = session?.features ?? null;
  return useCallback((pathname: string) => tenantHasRoute(features, pathname), [features]);
}
